import React, { useEffect, useState, useCallback } from "react";
import { useOutletContext } from "react-router-dom";
import adminAxios from "./adminAxios"; 
import socket from "../socket"; 
import { 
  Package, 
  Activity, 
  Search, 
  CheckCircle, 
  Clock, 
  Truck, 
  Trash2,
  DollarSign, 
  ChevronDown,
  ChevronUp,
  AlertCircle,
  Menu 
} from "lucide-react";

// Premium Station Palette
const colors = {
  primary: "#03f8d7", 
  accent: "#ff2d75",   
  surface: "rgba(13, 13, 18, 0.95)",
  deepBg: "#060608",
  border: "rgba(3, 248, 215, 0.15)",
  textMain: "#ffffff",
  textMuted: "#64748b",
  success: "#10B981",
  warning: "#F59E0B",
  info: "#3B82F6"
};

function ManageOrders() {
  const { isOpen: sidebarOpen, setIsOpen: setSidebarOpen } = useOutletContext();
  
  const [orders, setOrders] = useState([]);
  const [expandedId, setExpandedId] = useState(null);
  const [search, setSearch] = useState("");

  // Retrieve isolated tenant context identifier
  const tenantId = localStorage.getItem("adminId") || "global";

  const fetchOrders = useCallback(async () => {
    try {
      const res = await adminAxios.get("/api/orders", { params: { tenantId } });
      const data = Array.isArray(res.data) ? res.data : (res.data.orders || []);
      setOrders(data);
    } catch (err) {
      console.error("[System] Manifest Sync Error:", err);
      setOrders([]);
    }
  }, [tenantId]);

  useEffect(() => {
    fetchOrders();
    const interval = setInterval(fetchOrders, 30000); 
    return () => clearInterval(interval);
  }, [fetchOrders]);

  useEffect(() => {
    socket.emit("join", { userId: "admin", role: "admin", tenantId });

    const handleRefresh = () => {
      console.log("[Socket] Registry refresh triggered");
      fetchOrders();
    };

    const handleOrderUpdate = (updatedOrder) => {
      setOrders((prev) => 
        prev.map((o) => (String(o._id) === String(updatedOrder._id) ? updatedOrder : o))
      );
    };

    socket.on("registryRefresh", handleRefresh);
    socket.on("orderUpdate", handleOrderUpdate);

    return () => {
      socket.off("registryRefresh", handleRefresh);
      socket.off("orderUpdate", handleOrderUpdate);
    };
  }, [fetchOrders, tenantId]);

  const updateStatus = async (id, status) => {
    try {
      const targetId = String(id).trim();
      await adminAxios.put(`/api/orders/${targetId}/status`, { 
        status: status.toLowerCase(),
        tenantId 
      });
      fetchOrders();
    } catch (err) {
      console.error(err);
      alert("MANIFEST STATUS UPDATE FAILED");
    }
  };

  const updatePayment = async (id) => {
    try {
      const targetId = String(id).trim();
      await adminAxios.put(`/api/orders/${targetId}/status`, { 
        paymentStatus: "paid",
        tenantId 
      });
      fetchOrders();
    } catch (err) {
      console.error("[Settlement Error]", err);
      alert("SETTLEMENT FAILED");
    }
  };

  const deleteOrder = async (id) => {
    if (!window.confirm("PURGE MANIFEST: Confirm permanent deletion?")) return;
    try {
      const targetId = String(id).trim();
      await adminAxios.delete(`/api/orders/${targetId}`, { data: { tenantId } });
      setOrders(prev => prev.filter(o => String(o._id) !== targetId));
      if (expandedId === id) setExpandedId(null);
    } catch (err) {
      alert("PURGE ERROR: Entry remains in registry.");
    }
  };

  const toggleExpand = (id) => {
    setExpandedId(expandedId === id ? null : id);
  };

  const filteredOrders = orders.filter((o) => {
    const term = search.toLowerCase();
    const orderId = o._id ? String(o._id).toLowerCase() : "";
    const orderName = o.name ? o.name.toLowerCase() : "";
    const userEmail = o.userId?.email ? o.userId.email.toLowerCase() : "";
    
    return orderId.includes(term) || orderName.includes(term) || userEmail.includes(term);
  });

  return (
    <div style={styles.container} data-tenant-scope={tenantId}>
      <div style={{
        ...styles.mainContent,
        paddingLeft: sidebarOpen ? "280px" : "80px"
      }}>
        {/* HEADER SECTION */}
        <div style={styles.topBar}>
          <div style={styles.headerText}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <button 
                onClick={() => setSidebarOpen(!sidebarOpen)}
                style={styles.menuToggle}
              >
                <Menu size={20} color={colors.primary} />
              </button>
              <Package color={colors.primary} size={24} />
              <h1 style={styles.title}>LOGISTICS MANIFEST</h1>
            </div>
            <p style={styles.subTitle}>ASSET ASSIGNMENT & SETTLEMENT TELEMETRY</p>
          </div>
          <div style={styles.liveBadge}>
            <div style={styles.pulseDot}></div>
            <span style={styles.liveText}>UPLINK ACTIVE</span>
          </div>
        </div>

        <div style={styles.pagePadding}>
          {/* SEARCH BOX */}
          <div style={styles.searchWrapper}>
            <Search style={styles.searchIcon} size={18} />
            <input 
              style={styles.searchField}
              placeholder="SCAN FOR MANIFEST ID, ASSET NAME, OR NODE EMAIL..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          <div style={styles.listContainer}>
            {filteredOrders.length === 0 ? (
              <div style={styles.emptyBox}>
                <AlertCircle size={40} color={colors.textMuted} style={{ marginBottom: '15px' }} />
                <div>NO ACTIVE MANIFESTS IN REGISTRY</div>
              </div>
            ) : (
              filteredOrders.map((o) => {
                const statusLabel = normalizeStatus(o.status);
                const paymentLabel = normalizePayment(o.paymentStatus);
                const isOpen = expandedId === o._id;

                return (
                  <div key={String(o._id)} style={{
                    ...styles.orderCard, 
                    borderColor: isOpen ? colors.primary : colors.border,
                    background: isOpen ? "rgba(3, 248, 215, 0.02)" : colors.surface
                  }}>
                    <div style={styles.cardMainRow} onClick={() => toggleExpand(o._id)}>
                      
                      <div style={styles.infoSection}>
                        <div style={styles.productTitle}>{o.name || "UNNAMED ASSET"}</div>
                        <div style={{display: 'flex', gap: '10px', alignItems: 'center', marginTop: '6px'}}>
                            <code style={styles.idCode}>MFST // {o._id ? String(o._id).slice(-8).toUpperCase() : ""}</code>
                            <span style={styles.userTag}>{o.userId?.email || "Unknown_Node"}</span>
                        </div>
                      </div>

                      <div style={styles.financeSection}>
                        <div style={styles.dataLabel}>ASSET VALUE</div>
                        <div style={styles.priceValue}>
                          <span style={{ color: colors.primary }}>$</span>
                          {Number(o.price ?? 0).toLocaleString()}
                        </div>
                      </div>

                      <div style={styles.badgeSection}>
                        <StatusBadge status={statusLabel} />
                        <PaymentBadge status={paymentLabel} />
                      </div>

                      <div style={styles.controlsSection} onClick={(e) => e.stopPropagation()}>
                        <div style={styles.controlItem}>
                          <span style={styles.tinyLabel}>TRANSIT STATUS</span>
                          <select
                            value={statusLabel}
                            onChange={(e) => updateStatus(o._id, e.target.value)}
                            style={styles.modernSelect}
                          >
                            <option value="Assigned">Assigned</option>
                            <option value="Pending">Pending</option>
                            <option value="Processing">In Transit</option>
                            <option value="Delivered">Delivered</option>
                          </select>
                        </div>

                        <div style={styles.controlItem}>
                          <span style={styles.tinyLabel}>SETTLEMENT</span>
                          <button 
                            onClick={() => paymentLabel !== "Paid" && updatePayment(o._id)}
                            style={{
                              ...styles.paymentBtn, 
                              color: paymentLabel === "Paid" ? colors.success : colors.warning,
                              borderColor: paymentLabel === "Paid" ? colors.success : colors.warning
                            }}
                            disabled={paymentLabel === "Paid"}
                          >
                            {paymentLabel === "Paid" ? "CLEARED" : "PENDING"}
                          </button>
                        </div>
                        
                        <div style={styles.expandTrigger}>
                          {isOpen ? <ChevronUp size={20} color={colors.primary} /> : <ChevronDown size={20} color={colors.textMuted} />}
                        </div>
                      </div>
                    </div>

                    {isOpen && (
                      <div style={styles.expandSection}>
                        <AdminOrderDetails 
                          order={o} 
                          status={statusLabel} 
                          payment={paymentLabel} 
                          onDelete={() => deleteOrder(o._id)}
                        />
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>

      <style>{`
        @keyframes pulse {
          0% { box-shadow: 0 0 0 0px rgba(3, 248, 215, 0.4); }
          70% { box-shadow: 0 0 0 10px rgba(3, 248, 215, 0); }
          100% { box-shadow: 0 0 0 0px rgba(3, 248, 215, 0); }
        }
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}

function AdminOrderDetails({ order, status, payment, onDelete }) {
  return (
    <div style={styles.detailsBox}>
      <div style={styles.detailsGrid}>
        <div style={styles.detailCol}>
          <div style={styles.detailHeader}><Activity size={14} /> SYSTEM TRACE</div>
          <p style={styles.detailLine}><strong>REGISTRY_ID:</strong> <span style={styles.codeTxt}>{order._id}</span></p>
          <p style={styles.detailLine}><strong>DESCRIPTOR:</strong> {order.name || "-"}</p>
          <p style={styles.detailLine}><strong>BASE_VALUATION:</strong> ${order.price ?? 0}</p>
          <p style={styles.detailLine}><strong>NET_YIELD:</strong> <span style={{color: colors.primary}}>${order.profit ?? 0}</span></p>
        </div>
        <div style={styles.detailCol}>
          <div style={styles.detailHeader}><Package size={14} /> NODE DATA</div>
          <p style={styles.detailLine}><strong>IDENTITY:</strong> {order.userId?.name || "N/A"} ({order.userId?.email || "N/A"})</p>
          <p style={styles.detailLine}><strong>NODE_REF:</strong> {order.userId?._id || order.userId || "N/A"}</p>
          <p style={styles.detailLine}><strong>TIMESTAMP:</strong> {order.createdAt ? new Date(order.createdAt).toLocaleString() : "-"}</p>
          <div style={{display: 'flex', gap: '8px', marginTop: '12px'}}>
             <StatusBadge status={status} />
             <PaymentBadge status={payment} />
          </div>
        </div>
      </div>
      <div style={styles.detailActions}>
          <button onClick={onDelete} style={styles.purgeBtnLarge}>
            <Trash2 size={14} /> PURGE MANIFEST RECORD
          </button>
      </div>
    </div>
  );
}

function StatusBadge({ status }) {
  const icons = {
    "Assigned": <Clock size={10} />,
    "Pending": <Clock size={10} />,
    "Processing": <Truck size={10} />,
    "Delivered": <CheckCircle size={10} />
  };
  let color = colors.textMuted;
  if (status === "Pending") color = colors.warning;
  if (status === "Processing") color = colors.info;
  if (status === "Delivered" || status === "Completed") color = colors.success;

  return (
    <span style={{ ...styles.badgeBase, border: `1px solid ${color}`, color: color }}>
      {icons[status] || icons["Pending"]}
      {status.toUpperCase()}
    </span>
  );
}

function PaymentBadge({ status }) {
  const color = status === "Paid" ? colors.success : colors.accent;
  return (
    <span style={{ ...styles.badgeBase, border: `1px solid ${color}`, color: color }}>
      <DollarSign size={10} />
      {status === "Paid" ? "CLEARED" : "UNPAID"}
    </span>
  );
}

function normalizeStatus(status) {
  if (!status) return "Pending";
  const s = String(status).toLowerCase();
  if (s === "assigned") return "Assigned";
  if (s === "pending") return "Pending";
  if (s === "processing") return "Processing";
  if (s === "delivered") return "Delivered";
  if (s === "completed") return "Completed";
  return "Pending";
}

function normalizePayment(p) {
  if (!p) return "Unpaid";
  const s = String(p).toLowerCase();
  if (s === "paid") return "Paid";
  return "Unpaid";
}

const styles = {
  container: { display: "flex", minHeight: "100vh", backgroundColor: colors.deepBg, fontFamily: "'Inter', sans-serif" },
  mainContent: { flexGrow: 1, transition: "padding-left 0.3s ease" },
  menuToggle: { background: "none", border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", padding: "5px" },
  topBar: { 
    background: colors.surface, 
    backdropFilter: "blur(10px)",
    padding: "20px 40px", 
    display: "flex", 
    justifyContent: "space-between", 
    alignItems: "center", 
    borderBottom: `1px solid ${colors.border}`,
    position: 'sticky',
    top: 0,
    zIndex: 10
  },
  headerText: { display: "flex", flexDirection: "column" },
  title: { margin: 0, fontSize: "18px", fontWeight: "900", letterSpacing: "3px", color: "#fff" },
  subTitle: { margin: "4px 0 0 0", fontSize: "9px", color: colors.primary, fontWeight: "800", letterSpacing: "1px" },
  liveBadge: { display: "flex", alignItems: "center", gap: "10px", backgroundColor: "rgba(0,0,0,0.3)", padding: "8px 15px", borderRadius: "10px", border: `1px solid ${colors.border}` },
  pulseDot: { height: 8, width: 8, backgroundColor: colors.primary, borderRadius: "50%", animation: "pulse 2s infinite" },
  liveText: { fontSize: "9px", fontWeight: "900", color: colors.primary, letterSpacing: "1px" },
  pagePadding: { padding: "40px" },
  searchWrapper: { maxWidth: "800px", margin: "0 auto 40px auto", position: "relative" },
  searchIcon: { position: "absolute", left: "20px", top: "50%", transform: "translateY(-50%)", color: colors.primary },
  searchField: { 
    width: "100%", padding: "18px 20px 18px 55px", borderRadius: "15px", 
    background: "rgba(255,255,255,0.03)", border: `1px solid ${colors.border}`, 
    outline: "none", fontSize: "11px", fontWeight: "700", color: "#fff", letterSpacing: "1px" 
  },
  listContainer: { display: "flex", flexDirection: "column", gap: "12px", maxWidth: "1400px", margin: "0 auto" },
  orderCard: { 
    borderRadius: "15px", border: `1px solid ${colors.border}`, 
    overflow: "hidden", transition: "all 0.3s ease" 
  },
  cardMainRow: { padding: "20px 25px", display: "flex", alignItems: "center", justifyContent: "space-between", cursor: "pointer" },
  infoSection: { flex: 2 },
  productTitle: { fontSize: "14px", fontWeight: "900", color: "#fff", textTransform: "uppercase", letterSpacing: "0.5px" },
  userTag: { fontSize: "10px", color: colors.textMuted, fontWeight: "600" },
  idCode: { fontSize: "9px", color: colors.primary, background: "rgba(3, 248, 215, 0.05)", padding: "3px 8px", borderRadius: "6px", display: "inline-block", fontWeight: "800", fontFamily: "monospace", border: `1px solid ${colors.border}` },
  financeSection: { flex: 1, textAlign: "center" },
  dataLabel: { fontSize: "8px", color: colors.textMuted, fontWeight: "900", letterSpacing: "1px" },
  priceValue: { fontSize: "18px", fontWeight: "900", color: "#fff", marginTop: "4px", fontFamily: "monospace" },
  badgeSection: { flex: 1.5, display: "flex", flexDirection: "column", gap: "6px", alignItems: "center" },
  badgeBase: { padding: "4px 10px", borderRadius: "6px", fontSize: "8px", fontWeight: "900", display: "flex", alignItems: "center", gap: "6px", background: "rgba(0,0,0,0.2)" },
  controlsSection: { flex: 3.5, display: "flex", alignItems: "center", gap: "20px", justifyContent: "flex-end" },
  controlItem: { display: "flex", flexDirection: "column", gap: "6px" },
  tinyLabel: { fontSize: "8px", fontWeight: "900", color: colors.textMuted, letterSpacing: "1px" },
  modernSelect: { padding: "8px 12px", borderRadius: "8px", border: `1px solid ${colors.border}`, fontSize: "10px", fontWeight: "700", background: "#000", color: colors.primary, outline: 'none' },
  paymentBtn: { background: "rgba(0,0,0,0.2)", border: "1px solid", padding: "8px 12px", borderRadius: "8px", fontSize: "10px", fontWeight: "900", cursor: "pointer", transition: "0.2s" },
  expandTrigger: { padding: "5px", borderRadius: "50%", background: "rgba(255,255,255,0.03)" },
  expandSection: { background: "rgba(0,0,0,0.2)", borderTop: `1px solid ${colors.border}`, padding: "30px", animation: "fadeIn 0.4s ease" },
  detailsBox: { fontSize: "12px" },
  detailHeader: { fontSize: "9px", fontWeight: "900", color: colors.primary, marginBottom: "15px", display: "flex", alignItems: "center", gap: "8px", letterSpacing: "1px" },
  detailsGrid: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: "40px" },
  detailCol: { display: "flex", flexDirection: "column", gap: "10px" },
  detailLine: { margin: 0, color: "#fff", fontSize: "11px" },
  codeTxt: { fontFamily: "monospace", color: colors.primary, fontWeight: "700" },
  detailActions: { marginTop: "25px", paddingTop: "20px", borderTop: `1px solid ${colors.border}`, display: 'flex', justifyContent: 'flex-end' },
  purgeBtnLarge: { background: "rgba(255, 45, 117, 0.1)", color: colors.accent, border: `1px solid ${colors.accent}`, padding: "12px 24px", borderRadius: "10px", cursor: "pointer", fontWeight: "900", fontSize: "10px", letterSpacing: "1px", display: 'flex', alignItems: 'center', gap: '8px' },
  emptyBox: { textAlign: "center", padding: "80px", color: colors.textMuted, background: colors.surface, borderRadius: "20px", border: `1px solid ${colors.border}`, fontWeight: "800", letterSpacing: "1px" }
};

export default ManageOrders;